import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { categoryAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tag, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function CategoryManagerButton({ className = "" }) {
  const { workspaceId } = useParams();
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: "#7c3aed", order: 0 });

  const loadCategories = async () => {
    try {
      const res = await categoryAPI.list(workspaceId);
      setCategories(res.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (open) loadCategories();
  }, [open]);

  const openDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setForm({ name: category.name, description: category.description || "", color: category.color || "#7c3aed", order: category.order || 0 });
    } else {
      setEditingCategory(null);
      setForm({ name: "", description: "", color: "#7c3aed", order: categories.length });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingCategory) {
        await categoryAPI.update(workspaceId, editingCategory.id, form);
        toast.success("Catégorie modifiée");
      } else {
        await categoryAPI.create(workspaceId, form);
        toast.success("Catégorie créée");
      }
      loadCategories();
      setEditingCategory(null);
      setForm({ name: "", description: "", color: "#7c3aed", order: categories.length });
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (catId) => {
    try {
      await categoryAPI.delete(workspaceId, catId);
      toast.success("Catégorie supprimée");
      loadCategories();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <>
      <Button variant="outline" className={`gap-2 ${className}`} onClick={() => openDialog()} data-testid="manage-categories-btn">
        <Tag className="h-4 w-4" />
        Catégories
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nom *</label>
              <input
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Ex: DevOps"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <input
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Description optionnelle"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="flex gap-4">
              <div className="space-y-2 flex-1">
                <label className="text-sm font-medium">Couleur</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-10 w-14 rounded border cursor-pointer"
                  />
                  <span className="text-sm text-muted-foreground">{form.color}</span>
                </div>
              </div>
              <div className="space-y-2 flex-1">
                <label className="text-sm font-medium">Ordre</label>
                <input
                  type="number"
                  min="0"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            {/* Existing categories list */}
            {categories.length > 0 && !editingCategory && (
              <div className="border-t pt-4">
                <label className="text-sm font-medium text-muted-foreground">Catégories existantes</label>
                <div className="mt-2 space-y-1">
                  {categories.map(cat => (
                    <div key={cat.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/50">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: cat.color }} />
                      <span className="text-sm flex-1">{cat.name}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openDialog(cat)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(cat.id)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Annuler</Button>
              <Button className="flex-1" onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingCategory ? "Modifier" : "Créer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
